import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, Trash2, Plus } from 'lucide-react';
import api from '../lib/api';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = [
  { value: 'active',      label: 'Active' },
  { value: 'inactive',    label: 'Inactive' },
  { value: 'in_repair',   label: 'In Repair' },
  { value: 'retired',     label: 'Retired' },
  { value: 'lost_stolen', label: 'Lost/Stolen' },
];

export default function AssetFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [types, setTypes]   = useState([]);
  const [orgs, setOrgs]     = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving]   = useState(false);
  const [customFields, setCustomFields] = useState([]);

  const [form, setForm] = useState({
    name: '', asset_tag: '', type_id: '', status: 'active', org_id: '',
    serial_number: '', manufacturer: '', model: '', location: '',
    purchase_date: '', purchase_price: '', warranty_expiry: '', license_expiry: '',
    notes: '', alert_on_expiry: true,
    custom_values: {},
  });

  useEffect(() => {
    api.get('/assets/types').then(d => setTypes(d.types || [])).catch(() => {});
    api.get('/orgs').then(d => setOrgs(d.orgs || [])).catch(() => {});

    if (isEdit) {
      api.get(`/assets/${id}`).then(d => {
        const a = d.asset;
        setForm({
          name:            a.name || '',
          asset_tag:       a.asset_tag || '',
          type_id:         a.type_id || '',
          status:          a.status || 'active',
          org_id:          a.org_id || '',
          serial_number:   a.serial_number || '',
          manufacturer:    a.manufacturer || '',
          model:           a.model || '',
          location:        a.location || '',
          purchase_date:   a.purchase_date ? a.purchase_date.slice(0,10) : '',
          purchase_price:  a.purchase_price || '',
          warranty_expiry: a.warranty_expiry ? a.warranty_expiry.slice(0,10) : '',
          license_expiry:  a.license_expiry ? a.license_expiry.slice(0,10) : '',
          notes:           a.notes || '',
          alert_on_expiry: !!a.alert_on_expiry,
          custom_values:   a.custom_values || {},
        });
      }).catch(e => { toast.error(e.message); navigate('/assets'); }).finally(() => setLoading(false));
    }
  }, [id]);

  // Load custom fields when type changes
  useEffect(() => {
    if (!form.type_id) { setCustomFields([]); return; }
    const type = types.find(t => t.id === form.type_id);
    if (!type) { setCustomFields([]); return; }
    try {
      const fields = typeof type.custom_fields === 'string'
        ? JSON.parse(type.custom_fields)
        : (type.custom_fields || []);
      setCustomFields(fields);
    } catch { setCustomFields([]); }
  }, [form.type_id, types]);

  function set(key, value) { setForm(f => ({ ...f, [key]: value })); }
  function setCustom(key, value) { setForm(f => ({ ...f, custom_values: { ...f.custom_values, [key]: value } })); }

  async function save() {
    if (!form.name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      if (isEdit) await api.put(`/assets/${id}`, form);
      else        await api.post('/assets', form);
      toast.success(isEdit ? 'Asset updated' : 'Asset created');
      navigate('/assets');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function remove() {
    if (!window.confirm('Delete this asset? This cannot be undone.')) return;
    try {
      await api.delete(`/assets/${id}`);
      toast.success('Asset deleted');
      navigate('/assets');
    } catch (e) { toast.error(e.message); }
  }

  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:60 }}><div className="spinner" /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div className="page-header">
        <h2>{isEdit ? 'Edit Asset' : 'New Asset'}</h2>
      </div>

      <div className="page-body">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1100 }}>

        {/* Basic info */}
        <section style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>Basic Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Asset Name *</label>
              <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. MacBook Pro 14 — Dave" autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Asset Tag</label>
              <input className="form-input" value={form.asset_tag} onChange={e => set('asset_tag', e.target.value)} placeholder="e.g. GT-001" />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Asset Type</label>
              <select className="form-select" value={form.type_id} onChange={e => set('type_id', e.target.value)}>
                <option value="">Select type...</option>
                {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Organization</label>
              <select className="form-select" value={form.org_id} onChange={e => set('org_id', e.target.value)}>
                <option value="">No organization</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Location</label>
              <input className="form-input" value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Office 2, Desk 4" />
            </div>
          </div>
        </section>

        {/* Hardware details */}
        <section style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>Hardware Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Manufacturer</label>
              <input className="form-input" value={form.manufacturer} onChange={e => set('manufacturer', e.target.value)} placeholder="e.g. Apple, Dell, HP" />
            </div>
            <div className="form-group">
              <label className="form-label">Model</label>
              <input className="form-input" value={form.model} onChange={e => set('model', e.target.value)} placeholder="e.g. MacBook Pro 14-inch M3" />
            </div>
            <div className="form-group">
              <label className="form-label">Serial Number</label>
              <input className="form-input" value={form.serial_number} onChange={e => set('serial_number', e.target.value)} placeholder="Serial number" />
            </div>
          </div>
        </section>

        {/* Purchase & warranty */}
        <section style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>Purchase & Warranty</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Purchase Date</label>
              <input className="form-input" type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Purchase Price</label>
              <input className="form-input" type="number" min="0" step="0.01" value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label className="form-label">Warranty Expiry</label>
              <input className="form-input" type="date" value={form.warranty_expiry} onChange={e => set('warranty_expiry', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">License Expiry</label>
              <input className="form-input" type="date" value={form.license_expiry} onChange={e => set('license_expiry', e.target.value)} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginTop: 4 }}>
            <input type="checkbox" checked={form.alert_on_expiry} onChange={e => set('alert_on_expiry', e.target.checked)} />
            Send email + UI alert 30 days before warranty/license expiry
          </label>
        </section>

        {/* Custom fields */}
        {customFields.length > 0 && (
          <section style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 20 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>Custom Fields</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {customFields.map(field => (
                <div key={field.name} className="form-group">
                  <label className="form-label">{field.name}{field.required && ' *'}</label>
                  {field.type === 'textarea' ? (
                    <textarea className="form-textarea" rows={3} value={form.custom_values[field.name] || ''}
                      onChange={e => setCustom(field.name, e.target.value)} />
                  ) : field.type === 'date' ? (
                    <input className="form-input" type="date" value={form.custom_values[field.name] || ''}
                      onChange={e => setCustom(field.name, e.target.value)} />
                  ) : (
                    <input className="form-input" type={field.type === 'number' ? 'number' : 'text'}
                      value={form.custom_values[field.name] || ''}
                      onChange={e => setCustom(field.name, e.target.value)}
                      placeholder={field.placeholder || ''} />
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Notes */}
        <section style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>Notes</h3>
          <textarea className="form-textarea" rows={4} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any additional notes..." />
        </section>

        {/* Actions */}
        <div className="flex justify-between">
          <div className="flex gap-2">
            <button className="btn btn-primary" onClick={save} disabled={saving}><Save size={14} /> {saving ? 'Saving…' : 'Save Asset'}</button>
            <button className="btn btn-secondary" onClick={() => navigate('/assets')}>Cancel</button>
          </div>
          {isEdit && (
            <button className="btn btn-ghost" style={{ color: 'var(--danger)' }} onClick={remove}><Trash2 size={14} /> Delete</button>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
